import {useCallback, useMemo} from "react";

import {FetchPolicy, type Result} from "@khanacademy/wonder-blocks-data";
import {isTruthy} from "@khanacademy/wonder-stuff-core";
import {useMutation, useQuery} from "@khan/data-gql";

import * as toolDefinitions from "../definitions/all.ts";
import type {khanmigoToolsAvailableTeacherToolsType} from "../graphql/__graphql-types__/index.ts";
import {ADD_FAVORITED_TEACHER_TOOL_MUTATION} from "../graphql/add-favorited-tool-mutation.ts";
import {AVAILABLE_TEACHER_TOOLS_QUERY} from "../graphql/available-teacher-tools-query.ts";
import {DELETE_FAVORITED_TEACHER_TOOL_MUTATION} from "../graphql/delete-favorited-tool-mutation.ts";
import {FAVORITED_TEACHER_TOOLS_QUERY} from "../graphql/favorited-teacher-tools-query.ts";
import {type Tool} from "../types.ts";

const toolsByConfigName = Object.values(toolDefinitions).reduce((acc, tool) => {
    acc[tool.configName] = tool;
    return acc;
}, {});

const getToolId = (
    availableToolsResult: Result<
        khanmigoToolsAvailableTeacherToolsType["response"]
    >,
    configName: string,
) => {
    const toolId =
        availableToolsResult.status === "success" &&
        availableToolsResult.data?.aiGuideTeacherTools.find(
            (tool) => tool.configName === configName,
        )?.id;

    if (!toolId) {
        throw new Error(`tool with configName ${configName} not found`);
    }

    return toolId;
};

/**
 * Hook to query for available teacher tools and manage favorites. The tools
 * returned here are the tools that the current user has access to, given
 * their permissions, locale, etc. We also return details about the tools
 * that the user has favorited, with helpers to add and remove favorites.
 */
export const useAvailableTeacherTools = (): {
    availableTools: Tool[];
    favoriteToolConfigNames: Set<string>;
    addFavorite: (configName: string) => Promise<void>;
    deleteFavorite: (configName: string) => Promise<void>;
    toolLoadingStatus: Result<any>["status"];
    favoritesLoadingStatus: Result<any>["status"];
} => {
    const [availableToolsResult] = useQuery(AVAILABLE_TEACHER_TOOLS_QUERY, {
        fetchPolicy: FetchPolicy.CacheAndNetwork,
    });

    const [favoritedToolsResult, refetchFavorites] = useQuery(
        FAVORITED_TEACHER_TOOLS_QUERY,
        {fetchPolicy: FetchPolicy.CacheAndNetwork},
    );

    const [addFavoritedTool] = useMutation(ADD_FAVORITED_TEACHER_TOOL_MUTATION);

    const [deleteFavoritedTool] = useMutation(
        DELETE_FAVORITED_TEACHER_TOOL_MUTATION,
    );

    const addFavorite = useCallback(
        async (configName: string) => {
            const result = await addFavoritedTool({
                toolID: getToolId(availableToolsResult, configName),
                configName,
            });
            if (result.status === "success") {
                refetchFavorites();
            }
        },
        [addFavoritedTool, refetchFavorites, availableToolsResult],
    );

    const deleteFavorite = useCallback(
        async (configName: string) => {
            const result = await deleteFavoritedTool({
                toolID: getToolId(availableToolsResult, configName),
                configName,
            });
            if (result.status === "success") {
                refetchFavorites();
            }
        },
        [deleteFavoritedTool, refetchFavorites, availableToolsResult],
    );

    const favoriteToolConfigNames = useMemo(() => {
        const configNames = new Set<string>();
        if (favoritedToolsResult.status === "success") {
            for (const tool of favoritedToolsResult.data
                ?.aiGuideFavoritedTeacherTools ?? []) {
                if (tool) {
                    configNames.add(tool.configName);
                }
            }
        }
        return configNames;
    }, [favoritedToolsResult]);

    const availableTools: Tool[] = useMemo(() => {
        if (availableToolsResult.status !== "success") {
            return [];
        }

        // GraphQL only gives us the configName (+ id) of each tool, so we need
        // to map that to the full tool object. Other tool fields (like slug,
        // translated title, description, etc.) are defined on the frontend in
        // the tools/definitions directory.
        const gqlTools = availableToolsResult.data?.aiGuideTeacherTools ?? [];

        return gqlTools
            .map(({configName}) => toolsByConfigName[configName])
            .filter(isTruthy);
    }, [availableToolsResult]);

    return {
        availableTools,
        favoriteToolConfigNames,
        addFavorite,
        deleteFavorite,
        toolLoadingStatus: availableToolsResult.status,
        favoritesLoadingStatus: favoritedToolsResult.status,
    };
};
